/**
 * @fileoverview Subscription plan utilities.
 * @summary Helper functions for determining active user subscription plans.
 *
 * @package KaraoNow
 * @module apps/front/src/utils/planUtils
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { SubscriptionType } from '@karaonow/shared'
import type { User } from '../store/slices/authSlice'

/**
 * Determines the currently active subscription plan for an organizer.
 *
 * Logic:
 * - Returns the user's subscription type (FREE, PRO, PREMIUM, or ULTIMATE)
 * - ONE_SHOT events do NOT affect the user's subscription badge
 *   (ONE_SHOT is a per-event plan, not a user subscription)
 *
 * @param {User | null} user - The authenticated user
 * @returns {SubscriptionType} The active subscription plan
 */
export function getActivePlan(
  user: User | null,
): SubscriptionType {
  if (!user) return SubscriptionType.FREE

  // Return the user's subscription type
  const userPlanType = (user.subscription?.type as SubscriptionType) || SubscriptionType.FREE
  
  // ONE_SHOT is never returned here - it's a per-event plan, not a user plan
  // Return the actual subscription type (FREE, PRO, PREMIUM, or ULTIMATE)
  return userPlanType === SubscriptionType.ONE_SHOT ? SubscriptionType.FREE : userPlanType
}

/**
 * Gets the display label for a subscription plan (for badge display).
 *
 * @param {SubscriptionType} plan - The subscription plan
 * @returns {string} Display label for the badge
 */
export function getPlanBadgeLabel(plan: SubscriptionType): string {
  switch (plan) {
    case SubscriptionType.FREE:
      return 'Free'
    case SubscriptionType.ONE_SHOT:
      return '🎯'
    case SubscriptionType.PRO:
      return '⚡'
    case SubscriptionType.PREMIUM:
      return '⭐'
    case SubscriptionType.ULTIMATE:
      return '👑'
    default:
      return 'Free'
  }
}

/**
 * Gets the background color class for a subscription plan badge.
 *
 * @param {SubscriptionType} plan - The subscription plan
 * @returns {string} Tailwind CSS class for background color
 */
export function getPlanBadgeColor(plan: SubscriptionType): string {
  switch (plan) {
    case SubscriptionType.FREE:
      return 'bg-gray-600 text-white'
    case SubscriptionType.ONE_SHOT:
      return 'bg-indigo-600 text-white'
    case SubscriptionType.PRO:
      return 'bg-indigo-600 text-white'
    case SubscriptionType.PREMIUM:
      return 'bg-amber-500 text-white'
    case SubscriptionType.ULTIMATE:
      return 'bg-purple-600 text-white'
    default:
      return 'bg-gray-600 text-white'
  }
}

